import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, User, ChevronDown, HelpCircle, Crown, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTour } from "@/tour/TourContext";
import { usePro } from "@/context/ProContext";

export const Header = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { start: startTour } = useTour();
  const { isPro, pro } = usePro();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-gray-0/80 px-3 backdrop-blur-md sm:px-4 lg:px-8">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <button
          onClick={onMenuToggle}
          className="lg:hidden iso-btn-ghost !p-2 shrink-0"
          aria-label="Open menu"
          data-testid="header-menu-btn"
        >
          <Menu className="size-5" />
        </button>
        <div className="hidden sm:block">
          <div className="iso-chip-secondary iso-chip">
            <span className="size-1.5 rounded-full bg-secondary" />
            Season 2026 · Live
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 sm:gap-2">
        <button
          onClick={() => startTour(true)}
          className="iso-btn-ghost !py-1.5 !text-xs hidden sm:inline-flex"
          data-testid="header-start-tour"
          title="Take the product tour"
        >
          <HelpCircle className="size-3.5" /> Tour
        </button>
        <ThemeToggle />
        <button className="iso-btn-ghost !p-2 relative hidden sm:inline-flex" data-testid="notifications-btn" aria-label="Notifications">
          <Bell className="size-4" />
          <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-red" />
        </button>
        <div className="relative">
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-gray-100"
            data-testid="user-menu-btn"
          >
            <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gray-700 text-xs font-bold text-white relative">
              {user?.name?.[0]?.toUpperCase() ?? "U"}
              {isPro && (
                <span className="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-secondary text-white ring-2 ring-white" title="Pro member" data-testid="header-pro-badge">
                  <Crown className="size-2.5" />
                </span>
              )}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                {user?.name}
                {isPro && <span className="text-[9px] font-bold text-secondary">PRO</span>}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400">
                Legacy {user?.legacy_score ?? 0}
              </div>
            </div>
            <ChevronDown className="size-3 text-gray-400" />
          </button>
          {open && (
            <div
              className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-border bg-gray-0 shadow-card-lg"
              onMouseLeave={() => setOpen(false)}
              data-testid="user-menu-dropdown"
            >
              <div className="border-b border-border px-4 py-3">
                <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  {user?.name}
                  {isPro && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-1.5 py-0.5 text-[9px] font-bold text-secondary">
                      <Crown className="size-2.5" /> PRO
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">{user?.email}</div>
                {isPro && pro?.pro_until && (
                  <div className="mt-1 text-[10px] text-gray-400">
                    Pro until {new Date(pro.pro_until).toLocaleDateString()}
                  </div>
                )}
              </div>
              <Link to="/my-bets" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                <User className="size-4" /> My Bets
              </Link>
              <button
                onClick={() => { setOpen(false); startTour(true); }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                data-testid="menu-start-tour"
              >
                <HelpCircle className="size-4" /> Take the tour
              </button>
              <button
                onClick={() => { logout(); nav("/login"); }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red hover:bg-red-lighter/40"
                data-testid="logout-btn"
              >
                <LogOut className="size-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
